/** Utilities for calculating daily task statistics and project breakdowns. */
import { getTaskProjects } from '@/features/tasks/utils/data/taskTags';
import { translate as t } from '@/i18n/useT';

const PERCENT_BASE = 100;

/**
 * Calculates the total duration of incomplete tasks for a specific user.
 *
 * @param {import('@/types').Task[]} allTasks - The full list of tasks.
 * @param {string} userId - The ID of the user.
 * @returns {number} The total duration in minutes.
 */
export const calculateUncompletedDuration = (allTasks, userId) => {
  const parentIds = new Set();
  allTasks.forEach((t) => {
    if (t.parentId) parentIds.add(t.parentId);
  });

  const uncompletedTasks = allTasks.filter(
    (task) =>
      task.userId === userId && !task.completed && !parentIds.has(task.id),
  );

  return uncompletedTasks.reduce((total, task) => {
    if (task.duration && typeof task.duration === 'number') {
      return total + task.duration;
    }
    return total;
  }, 0);
};

/**
 * Calculates today's progress and project breakdown.
 *
 * @param {import('@/types').Task[]} allTasks - The full list of tasks.
 * @param {Map<string, string>} projectDisplayMap - Map of project IDs to display names.
 * @param {number} gcalDuration - Total duration of GCal events for today in minutes (optional).
 * @returns {{ completedDuration: number, totalDuration: number, percentage: number, projectBreakdown: Array<{ id: string, name: string, completedDuration: number, totalDuration: number }>, gcalDuration: number }}
 */
export const calculateDailyMetrics = (
  allTasks,
  projectDisplayMap,
  gcalDuration = 0,
) => {
  const today = new Date().toISOString().split('T')[0];

  const todayTasks = (allTasks ?? []).filter((task) => {
    if (task.archived) return false;
    if (!task.date) return false;
    const d = task.date.toDate ? task.date.toDate() : new Date(task.date);
    return d.toISOString().split('T')[0] === today;
  });

  const totalDuration = todayTasks.reduce(
    (sum, t) => sum + (t.duration || 0),
    0,
  );
  const completedDuration = todayTasks
    .filter((t) => t.completed)
    .reduce((sum, t) => sum + (t.duration || 0), 0);
  const percentage =
    totalDuration > 0
      ? Math.round((completedDuration / totalDuration) * PERCENT_BASE)
      : 0;

  // Group by project for the time distribution visualization
  const breakdownMap = {};
  todayTasks.forEach((task) => {
    const projectIds = getTaskProjects(task);
    const primaryProjectId =
      projectIds.length > 0 ? projectIds[0].toLowerCase() : 'no-project';
    const projectName =
      primaryProjectId === 'no-project'
        ? t('taskmetrics.noProject', 'No project')
        : projectDisplayMap?.get(primaryProjectId) || primaryProjectId;

    if (!breakdownMap[primaryProjectId]) {
      breakdownMap[primaryProjectId] = {
        id: primaryProjectId,
        name: projectName,
        completedDuration: 0,
        totalDuration: 0,
      };
    }

    breakdownMap[primaryProjectId].totalDuration += task.duration || 0;
    if (task.completed) {
      breakdownMap[primaryProjectId].completedDuration += task.duration || 0;
    }
  });

  const projectBreakdown = Object.values(breakdownMap).sort(
    (a, b) => b.totalDuration - a.totalDuration || a.name.localeCompare(b.name),
  );

  return {
    completedDuration,
    totalDuration,
    percentage,
    projectBreakdown,
    gcalDuration,
  };
};
